import trace from '~/lib/trace';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import moment from 'moment';

import sy from '~/styles';
import {Text, InputChat} from '~/components/controls';
import {service_request as serviceRequestApi} from '~/api/private';
import {isLessorStatus} from '~/types/rob';
import {STATUS_CODES} from '~/types/statuses';
import {Activity, Message} from '~/components/shared';
import PropTypes from 'prop-types';

const MessagesTab = ({
  focused,
  request_id,
  overview,
  is_retail_car,
  lessor_status = overview?.rob?.status,
  cancelled_read_only = false,
  require_request_id = false,
}) => {
  const [messages, setMessages] = useState([]);
  const [editable, setEditable] = useState(true);
  const [pendingMessage, setPendingMessage] = useState(null);

  const fetch = useCallback(async () => {
    if (require_request_id && !request_id) return;
    const {result} = await serviceRequestApi.messages(request_id);
    setMessages(result.messages);
  }, [request_id, require_request_id]);

  useEffect(() => {
    if (focused) {
      fetch().catch(trace);
    }
  }, [focused, fetch]);

  useEffect(() => {
    if (overview) {
      setEditable(
        !isLessorStatus(lessor_status) &&
          (!cancelled_read_only ||
            overview.status !== STATUS_CODES.Cancelled) &&
          overview.status !== STATUS_CODES.Done &&
          overview.status !== STATUS_CODES.Invoiced &&
          overview.status !== STATUS_CODES.Archived,
      );
    }
  }, [overview, lessor_status, cancelled_read_only]);

  const scrollViewRef = useRef(null);

  const onSend = async () => {
    if (!editable) {
      return;
    }

    const {success} = await serviceRequestApi.send_message(
      request_id,
      pendingMessage,
    );

    if (success) {
      setPendingMessage(null);
      await fetch();
    }
  };

  const onAttachment = async (attachment) => {
    if (!editable) {
      return;
    }

    const {success} = await serviceRequestApi.send_attachment(
      request_id,
      attachment,
    );

    if (success) {
      await fetch();
    }
  };

  const onEditMessage = async (message_id, value) => {
    const {success} = await serviceRequestApi.edit_message(request_id, {
      id: message_id,
      content: value,
    });

    if (success) {
      await fetch();
    }
  };

  const onDeleteMessage = async (message_id) => {
    const {success} = await serviceRequestApi.delete_message(request_id, {
      id: message_id,
    });

    if (success) {
      await fetch();
    }
  };

  const last_activity_id = Object.values(messages ?? {})
    .flat()
    .filter((item) => item.activity_id)
    .filter(
      (item) =>
        item.status !== 'DATE_CHANGED' &&
        item.status !== 'TIME_CHANGED' &&
        item.status !== 'EXPECTED_COMPLETION_DATE_CHANGED',
    )
    .slice(-1)?.[0]?.activity_id;

  if (!overview) {
    return <></>;
  }

  return (
    <>
      <ScrollView
        style={[sy['flex-1'], sy['p-4'], sy['bg-gray95']]}
        contentContainerStyle={[sy['gap-4']]}
        ref={scrollViewRef}
        onContentSizeChange={() =>
          scrollViewRef.current?.scrollToEnd({animated: true})
        }>
        {messages &&
          Object.keys(messages).map((key) => (
            <View key={key} style={[sy['gap-2']]}>
              <Text
                style={[
                  sy.smallPlus,
                  {
                    textAlign: 'center',
                  },
                ]}>
                {moment(key, 'DD-MM-YYYY').format('D MMMM YYYY')}
              </Text>
              <View style={[sy['gap-4']]}>
                {messages[key].map((item) => {
                  const {
                    activity_id,
                    status,
                    timestamp,
                    attributes,
                    ...message
                  } = item;

                  const display_timestamp = moment();
                  display_timestamp.set(
                    'hours',
                    moment(timestamp).get('hours'),
                  );
                  display_timestamp.set(
                    'minutes',
                    moment(timestamp).get('minutes'),
                  );

                  if (activity_id) {
                    return (
                      <Activity
                        key={activity_id}
                        request_id={request_id}
                        parent_id={item.parent_id}
                        request_type={item.request_type}
                        activity_id={activity_id}
                        attributes={attributes}
                        status={status}
                        display_timestamp={display_timestamp}
                        is_last_activity={activity_id === last_activity_id}
                      />
                    );
                  }

                  const message_editable =
                    editable && message?.user_id && !message?.is_sent;

                  return (
                    <Message
                      key={message.message_id}
                      style={[sy['rounded-lg'], sy['p-2']]}
                      {...message}
                      timestamp={display_timestamp}
                      onEdit={message_editable ? onEditMessage : null}
                      onDelete={message_editable ? onDeleteMessage : null}
                    />
                  );
                })}
              </View>
            </View>
          ))}
      </ScrollView>
      {editable && !is_retail_car && (
        <>
          <Divider style={sy['bg-gray88']} />
          <InputChat
            style={[sy['bg-white']]}
            message={pendingMessage}
            onChange={setPendingMessage}
            onSend={onSend}
            onAttachment={onAttachment}
          />
        </>
      )}
    </>
  );
};
MessagesTab.propTypes = {
  focused: PropTypes.any,
  request_id: PropTypes.any,
  overview: PropTypes.any,
  is_retail_car: PropTypes.any,
  lessor_status: PropTypes.any,
  cancelled_read_only: PropTypes.bool,
  require_request_id: PropTypes.bool,
};

export default MessagesTab;
